import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Trash2, Eye } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

interface Inquiry {
    id: number;
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
    created_at: string;
}

export default function InquiriesManager() {
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
    const { toast } = useToast();

    useEffect(() => {
        fetchInquiries();
    }, []);

    const fetchInquiries = async () => {
        try {
            const data = await fetchAPI("?type=inquiries");
            setInquiries(data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm("Delete this inquiry?")) return;
        try {
            const result = await fetchAPI(`?type=inquiries&id=${id}`, { method: "DELETE" });
            if (result.status === "success") {
                toast({ title: "Deleted", description: "Inquiry removed" });
                fetchInquiries();
            } else {
                throw new Error(result.message);
            }
        } catch (error: any) {
            toast({ variant: "destructive", title: "Cannot Delete", description: error.message });
        }
    };

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-serif font-bold">Inquiries</h1>
            <div className="border rounded-lg bg-card text-card-foreground shadow-sm">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Email/Phone</TableHead>
                            <TableHead>Subject</TableHead>
                            <TableHead>Message</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} className="text-center py-4">Loading...</TableCell></TableRow>
                        ) : inquiries.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="text-center py-4 text-muted-foreground">No inquiries found.</TableCell></TableRow>
                        ) : inquiries.map(inquiry => (
                            <TableRow key={inquiry.id}>
                                <TableCell className="whitespace-nowrap">{new Date(inquiry.created_at).toLocaleDateString()}</TableCell>
                                <TableCell>{inquiry.name}</TableCell>
                                <TableCell>
                                    <div className="text-sm">{inquiry.email}</div>
                                    <div className="text-xs text-muted-foreground">{inquiry.phone}</div>
                                </TableCell>
                                <TableCell>{inquiry.subject}</TableCell>
                                <TableCell className="max-w-md truncate" title={inquiry.message}>{inquiry.message}</TableCell>
                                <TableCell className="text-right">
                                    <Button variant="ghost" size="icon" onClick={() => setSelectedInquiry(inquiry)}>
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => handleDelete(inquiry.id)}>
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={!!selectedInquiry} onOpenChange={(open) => !open && setSelectedInquiry(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Inquiry Details</DialogTitle>
                        <DialogDescription>
                            Received on {selectedInquiry && new Date(selectedInquiry.created_at).toLocaleString()}
                        </DialogDescription>
                    </DialogHeader>
                    {selectedInquiry && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="font-semibold text-sm text-muted-foreground">Name</h4>
                                    <p>{selectedInquiry.name}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-sm text-muted-foreground">Email</h4>
                                    <p>{selectedInquiry.email}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-sm text-muted-foreground">Phone</h4>
                                    <p>{selectedInquiry.phone || "-"}</p>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-sm text-muted-foreground">Subject</h4>
                                    <p>{selectedInquiry.subject}</p>
                                </div>
                            </div>
                            <div>
                                <h4 className="font-semibold text-sm text-muted-foreground mb-2">Message</h4>
                                <div className="bg-muted p-4 rounded-lg text-sm whitespace-pre-wrap leading-relaxed">
                                    {selectedInquiry.message}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
